import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import gsap from "gsap";
import { ArrowRight, ChevronDown, Navigation, BookOpen, Mail } from "react-feather";
import { useMousePosition } from "../../hooks/useMousePosition";
import HeroLaunchStrip from "./hero/HeroLaunchStrip";
import HeroProductVisual from "./hero/HeroProductVisual";
import MagneticButton from "./hero/MagneticButton";
import "../../styles/hero-premium.css";

const PARTICLES = Array.from({ length: 24 }, (_, i) => ({
  id: i,
  left: `${(i * 17 + 7) % 100}%`,
  top: `${(i * 23 + 11) % 100}%`,
  delay: `${(i % 5) * 1.2}s`,
  size: i % 3 === 0 ? 3 : 2,
}));

export default function PremiumHero() {
  const sectionRef = useRef(null);
  const headlineRef = useRef(null);
  const subRef = useRef(null);
  const glowRef = useRef(null);
  const mouse = useMousePosition(sectionRef);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-reveal-line", {
        y: 60,
        opacity: 0,
        duration: 1,
        stagger: 0.12,
        ease: "power3.out",
        delay: 0.15,
      });
      gsap.from(subRef.current, {
        y: 30,
        opacity: 0,
        duration: 0.8,
        delay: 0.55,
        ease: "power2.out",
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    if (!glowRef.current) return;
    glowRef.current.style.left = `${mouse.x}px`;
    glowRef.current.style.top = `${mouse.y}px`;
  }, [mouse.x, mouse.y]);

  return (
    <section
      ref={sectionRef}
      className="hero-premium relative min-h-screen flex flex-col justify-center overflow-hidden"
    >
      {/* Background layers */}
      <div className="hero-aurora" />
      <div className="absolute inset-0 hero-mesh" />
      <div className="absolute inset-0 hero-grid-lines opacity-80" />
      <div
        ref={glowRef}
        className="hero-mouse-glow"
        style={{ left: 0, top: 0 }}
        aria-hidden
      />

      {/* Floating orbs */}
      <div className="absolute top-[15%] right-[10%] w-[320px] h-[320px] rounded-full bg-[#2563EB]/30 blur-[100px] animate-pulse pointer-events-none" />
      <div className="absolute bottom-[20%] left-[5%] w-[280px] h-[280px] rounded-full bg-[#F5C044]/20 blur-[90px] pointer-events-none" />
      <div className="absolute top-[40%] left-[30%] w-[200px] h-[200px] rounded-full bg-[#1d4ed8]/25 blur-[80px] pointer-events-none" />

      {/* Particles */}
      {PARTICLES.map((p) => (
        <span
          key={p.id}
          className="hero-particle pointer-events-none"
          style={{
            left: p.left,
            top: p.top,
            width: p.size,
            height: p.size,
            animationDelay: p.delay,
          }}
        />
      ))}

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 lg:pt-28 pb-16 lg:pb-20">
        <div className="grid lg:grid-cols-2 gap-10 xl:gap-16 items-center">
          {/* Left — Content */}
          <div className="order-2 lg:order-1">
            <HeroLaunchStrip />

            <h1
              ref={headlineRef}
              className="text-[2.5rem] sm:text-5xl lg:text-6xl xl:text-[4.25rem] font-extrabold text-white leading-[1.05] tracking-tight"
            >
              <span className="hero-reveal-line block">Empowering the</span>
              <span className="hero-reveal-line block">Future Through</span>
              <span className="hero-reveal-line block mt-1">
                <span className="hero-gradient-text">Technology</span>
              </span>
            </h1>

            <p
              ref={subRef}
              className="mt-6 text-lg sm:text-xl text-white/85 max-w-xl leading-relaxed font-medium"
            >
              Smart Mobility, Digital Education &{" "}
              <span className="text-white">Scalable Software Solutions</span>
            </p>

            <p className="mt-4 text-sm text-white/50 max-w-md">
              EPT Pravahaa — building next-generation platforms for mobility, learning, and enterprise technology.
            </p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.5 }}
              className="mt-10 flex flex-wrap gap-3 sm:gap-4"
            >
              <MagneticButton to="/rudra-rides" variant="primary">
                <Navigation size={18} />
                Explore Rudra Ride
                <ArrowRight size={16} />
              </MagneticButton>
              <MagneticButton to="/edupravahaa" variant="gold">
                <BookOpen size={18} />
                Explore Edu Pravahaa
              </MagneticButton>
              <MagneticButton to="/contact" variant="glass">
                <Mail size={18} />
                Contact Us
              </MagneticButton>
            </motion.div>

            {/* Mobile product preview */}
            <div className="mt-12 lg:hidden">
              <HeroProductVisual />
            </div>
          </div>

          {/* Right — Visuals */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="order-1 lg:order-2 hidden lg:block"
          >
            <HeroProductVisual />
          </motion.div>
        </div>

        <motion.a
          href="#launch-event"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/40 hover:text-[#F5C044] transition-colors"
        >
          <span className="text-[10px] uppercase tracking-[0.25em]">Discover</span>
          <ChevronDown size={22} className="animate-bounce" />
        </motion.a>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#071120] to-transparent pointer-events-none" />
    </section>
  );
}
